import { useState, useEffect, useCallback } from "react";
import { knowledgeAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Brain, Plus, Trash2, Pencil, Loader2, Sparkles,
  HelpCircle, AlertTriangle, Tag, Info, Utensils, Search
} from "lucide-react";

const CATEGORIES = [
  { value: "faq", label: "FAQ", icon: HelpCircle, color: "text-blue-600", bg: "bg-blue-50" },
  { value: "allergen", label: "Allergen", icon: AlertTriangle, color: "text-red-600", bg: "bg-red-50" },
  { value: "policy", label: "Policy", icon: Info, color: "text-violet-600", bg: "bg-violet-50" },
  { value: "special", label: "Special", icon: Utensils, color: "text-amber-600", bg: "bg-amber-50" },
  { value: "info", label: "Info", icon: Tag, color: "text-emerald-600", bg: "bg-emerald-50" },
];

function getCatInfo(cat) {
  return CATEGORIES.find(c => c.value === cat) || CATEGORIES[0];
}

export default function KnowledgePage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  // Add/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formCat, setFormCat] = useState("faq");
  const [formQuestion, setFormQuestion] = useState("");
  const [formAnswer, setFormAnswer] = useState("");
  const [formTags, setFormTags] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await knowledgeAPI.list();
      setEntries(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleAIGenerate = async () => {
    setGenerating(true);
    try {
      const res = await knowledgeAPI.aiGenerate();
      toast.success(`Generated ${res.data.count} knowledge entries!`);
      await fetchEntries();
    } catch (err) {
      toast.error("Failed to generate knowledge graph");
    } finally {
      setGenerating(false);
    }
  };

  const openAddDialog = () => {
    setEditingId(null);
    setFormCat("faq"); setFormQuestion(""); setFormAnswer(""); setFormTags("");
    setDialogOpen(true);
  };

  const openEditDialog = (entry) => {
    setEditingId(entry.id);
    setFormCat(entry.category);
    setFormQuestion(entry.question || "");
    setFormAnswer(entry.answer || "");
    setFormTags((entry.tags || []).join(", "));
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formAnswer) { toast.error("Answer is required"); return; }
    setSaving(true);
    const data = { category: formCat, question: formQuestion, answer: formAnswer, tags: formTags.split(",").map(t => t.trim()).filter(Boolean) };
    try {
      if (editingId) {
        await knowledgeAPI.update(editingId, data);
        toast.success("Entry updated");
      } else {
        await knowledgeAPI.create(data);
        toast.success("Entry added");
      }
      setDialogOpen(false);
      await fetchEntries();
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await knowledgeAPI.delete(id);
      setEntries(prev => prev.filter(e => e.id !== id));
      toast.success("Entry deleted");
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const filtered = entries.filter(e => {
    if (filter !== "all" && e.category !== filter) return false;
    if (search) {
      const s = search.toLowerCase();
      return (e.question?.toLowerCase().includes(s) || e.answer?.toLowerCase().includes(s) || e.tags?.some(t => t.toLowerCase().includes(s)));
    }
    return true;
  });

  const catCounts = {};
  entries.forEach(e => { catCounts[e.category] = (catCounts[e.category] || 0) + 1; });

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-5 fade-in" data-testid="knowledge-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Knowledge Graph</h1>
          <p className="text-sm text-slate-500 mt-0.5">{entries.length} entries — powers your AI agent's answers</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-9 text-xs" onClick={handleAIGenerate} disabled={generating} data-testid="ai-generate-knowledge">
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5 text-amber-500" />}
            AI Generate
          </Button>
          <Button size="sm" className="h-9 text-xs shadow-sm shadow-primary/20" onClick={openAddDialog} data-testid="add-knowledge-entry">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Entry
          </Button>
        </div>
      </div>

      {/* Category pills + search */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1.5">
          <button onClick={() => setFilter("all")} className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors ${filter === "all" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`} data-testid="filter-all">
            All ({entries.length})
          </button>
          {CATEGORIES.map(c => (
            <button key={c.value} onClick={() => setFilter(c.value)} className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors ${filter === c.value ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`} data-testid={`filter-${c.value}`}>
              {c.label} ({catCounts[c.value] || 0})
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search entries..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9 text-sm" data-testid="knowledge-search" />
        </div>
      </div>

      {/* Entries */}
      {filtered.length === 0 ? (
        <Card className="border-slate-100 border-dashed">
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 rounded-2xl bg-primary/5 flex items-center justify-center mx-auto mb-4">
              <Brain className="w-7 h-7 text-primary/60" />
            </div>
            <p className="text-base font-semibold text-slate-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {entries.length === 0 ? "No knowledge entries yet" : "No matching entries"}
            </p>
            <p className="text-sm text-slate-400 mt-1 mb-5 max-w-sm mx-auto">
              {entries.length === 0 ? "Add entries manually or let AI generate a starter knowledge graph from your restaurant's info." : "Try adjusting your search or filter."}
            </p>
            {entries.length === 0 && (
              <Button onClick={handleAIGenerate} disabled={generating} className="shadow-sm shadow-primary/20">
                {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2 text-amber-500" />}
                AI Generate Knowledge Graph
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {filtered.map(entry => {
            const catInfo = getCatInfo(entry.category);
            const Icon = catInfo.icon;
            return (
              <Card key={entry.id} className="border-slate-100 hover:border-slate-200 transition-colors" data-testid={`knowledge-entry-${entry.id}`}>
                <CardContent className="p-4 flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-lg ${catInfo.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                    <Icon className={`w-4 h-4 ${catInfo.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    {entry.question && (
                      <p className="text-[13px] font-semibold text-slate-800 mb-0.5">{entry.question}</p>
                    )}
                    <p className="text-[13px] text-slate-600 leading-relaxed">{entry.answer}</p>
                    {entry.tags?.length > 0 && (
                      <div className="flex gap-1 mt-2 flex-wrap">
                        {entry.tags.map((tag, i) => (
                          <Badge key={i} variant="secondary" className="text-[9px] bg-slate-100 text-slate-500 px-1.5 py-0">{tag}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600" onClick={() => openEditDialog(entry)} data-testid={`edit-knowledge-${entry.id}`}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-500" onClick={() => handleDelete(entry.id)} data-testid={`delete-knowledge-${entry.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="knowledge-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{editingId ? "Edit Entry" : "Add Knowledge Entry"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm">Category</Label>
              <Select value={formCat} onValueChange={setFormCat}>
                <SelectTrigger data-testid="knowledge-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Question (optional)</Label>
              <Input placeholder="What are your hours?" value={formQuestion} onChange={(e) => setFormQuestion(e.target.value)} data-testid="knowledge-question" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Answer *</Label>
              <Textarea placeholder="We're open Monday through Saturday, 11am to 10pm..." value={formAnswer} onChange={(e) => setFormAnswer(e.target.value)} rows={3} data-testid="knowledge-answer" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Tags (comma separated)</Label>
              <Input placeholder="hours, schedule, timing" value={formTags} onChange={(e) => setFormTags(e.target.value)} data-testid="knowledge-tags" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-knowledge-entry">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
              {editingId ? "Update" : "Add Entry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
